<template>
  <div class="batches-modal">
    <div class="batches-modal-body">
      <batch-move-results-table
        v-if="showResultsView"
        :results="moveResults"
        :summary="moveSummary"
        :is-retrying="isMoving"
        :close-label="closeLabelValue"
        :target-batch-identifier="createdBatchID"
        @retry-failed="handleRetryFailed"
        @close="handleResultsClose"
        @open-batch="handleOpenBatch"
      />

      <template v-else>
        <spinner v-if="isCreatingBatch" />

        <batch-move-overload-view
          v-else-if="showOverloadView"
          :retry-after="moveRetryAfter"
          @retry="handleCreateNewBatch"
          @close="handleOverloadClose"
        />

        <div v-else class="batches-list">
          <template v-if="isFetchingBatches">
            <inline-batch-card-skeleton
              v-for="index in 3"
              :key="index"
            />
          </template>

          <template v-else-if="batches.length">
            <inline-batch-card
              v-for="batch in batches"
              :key="batch.id"
              :batch="batch"
              class="batches-list-item"
              @click="handleChooseBatch(batch)"
            />
          </template>

          <div
            v-else
            class="batches-list-empty"
          >
            <svg-loader
              src="/static/images/icon-empty-list.svg"
              class="batches-list-empty-icon"
            />

            <div v-if="hasFetchingErrors">
              <div class="batches-list-empty-text">
                {{ fetchingErrorMessage }}
              </div>
            </div>

            <template v-else>
              <div class="batches-list-empty-text">
                Não há lotes compatíveis. Você pode criar um novo lote (Se alguma das amostras selecionadas já estiver em um lote, ela será movida para o lote novo).
              </div>

              <md-button
                class="btn btn-primary"
                @click="handleCreateNewBatch"
              >
                Criar novo lote
              </md-button>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import batchService from '@/api/batch';
import notify from '@/api/notification'
import { MAX_SAMPLES_PER_BATCH } from '@/constants/batch'
import { translateBatchErrorFromResponse, resolveConflictToastTitle } from '@/utils/batchErrorCodes'

import { useMoveSamplesBatch } from '@/composables/useMoveSamplesBatch'

import Spinner from '@/components/common/Spinner'

import InlineBatchCard from './InlineBatchCard'
import InlineBatchCardSkeleton from './InlineBatchCardSkeleton'
import BatchMoveOverloadView from './BatchMoveOverloadView.vue'
import BatchMoveResultsTable from './BatchMoveResultsTable.vue'
import { isWsSampleOrigin } from '@/components/Batch/batchSource'

export default {
  name: 'BatchSamplesBatchSelection',
  components: {
    InlineBatchCard,
    InlineBatchCardSkeleton,
    Spinner,
    BatchMoveOverloadView,
    BatchMoveResultsTable,
  },
  props: {
    sample: {
      type: Object
    }
  },
  setup() {
    const {
      summary,
      results,
      retryAfter,
      lastError,
      moveSamplesBatch,
      retryFailed,
      reset,
    } = useMoveSamplesBatch()

    return {
      moveSummary: summary,
      moveResults: results,
      moveRetryAfter: retryAfter,
      moveLastError: lastError,
      moveSamplesBatch,
      retryFailed,
      resetMoveState: reset,
    }
  },
  computed: {
    ...mapGetters(['selectedSamples']),
    targetSamples() {
      return this.sample ? [this.sample] : this.selectedSamples
    },
    closeLabelValue() {
      return this.moveSummary && this.moveSummary.targetBatchIdentifier
        ? 'Ver lote criado'
        : 'Fechar'
    },
    modalTitle() {
      if (this.showResultsView) return 'Resultado do loteamento'
      return 'Selecione o lote'
    },
    fetchingErrorMessage() {
      const fallback = 'Ocorreu um erro ao tentar buscar por lotes compatíveis.'
      if (!this.fetchingError) return fallback
      const translated = translateBatchErrorFromResponse(this.fetchingError)
      if (translated) return translated
      if (this.fetchingError.status === 422) {
        return this.translateBatchSampleValdation(this.fetchingError)
      }
      return fallback
    },
  },
  data() {
    return {
      batches: [],
      isFetchingBatches: false,
      isCreatingBatch: false,
      isMoving: false,
      hasFetchingErrors: false,
      fetchingError: null,
      showOverloadView: false,
      showResultsView: false,
      createdBatchID: null,
    }
  },
  watch: {
    modalTitle: {
      immediate: true,
      handler(value) {
        this.$emit('update:title', value)
      },
    },
  },
  mounted() {
    this.fetchBatches()
  },
  beforeDestroy() {
    this.resetMoveState()
  },
  methods: {
    async handleChooseBatch(batch) {
      this.$emit('continue', batch)
    },
    async fetchBatches() {
      this.isFetchingBatches = true;
      this.hasFetchingErrors = false;
      this.fetchingError = null;

      try {
        const payload = this.targetSamples.map(sample => sample.bar_code)
        const batchesResponse = await batchService.getBatchesCompatibleWithSamples(payload)

        this.batches = batchesResponse.data ?? []
      } catch (error) {
        console.error('Error fetching compatible batches.', error)

        this.hasFetchingErrors = true;
        this.fetchingError = error?.response || error;

        notify({
          title: 'Erro:',
          message: 'Ocorreu um problema inesperado ao buscar por lotes disponíveis.'
        })
      }

      this.isFetchingBatches = false
    },
    translateBatchSampleValdation({ data }) {
      return data.type === 'validation' ? data?.description : data?.message
    },
    buildItems() {
      return this.targetSamples.map(sample => {
        const item = { barcode: sample.bar_code }

        if (sample.batch_identifier) {
          item.sourceBatchIdentifier = sample.batch_identifier
        }

        return item
      })
    },
    resolveTargetBatchType() {
      return this.targetSamples.some(sample => isWsSampleOrigin(sample.origem)) ? 'AOLWS' : 'B2B'
    },
    async handleCreateNewBatch() {
      const items = this.buildItems()

      if (items.length > MAX_SAMPLES_PER_BATCH) {
        notify({
          title: 'Limite excedido',
          message: `Selecione no máximo ${MAX_SAMPLES_PER_BATCH} amostras por lote.`,
        })
        return
      }

      this.isCreatingBatch = true
      this.showOverloadView = false

      const type = this.resolveTargetBatchType()
      const outcome = await this.moveSamplesBatch(items, {
        targetBatchCreation: { type },
      })

      this.isCreatingBatch = false

      this.applyOutcome(outcome)
    },
    applyOutcome(outcome) {
      const summaryTargetBatchID = this.moveSummary?.targetBatchIdentifier

      switch (outcome) {
        case 'SUCCESS_TOTAL': {
          const targetBatchID = summaryTargetBatchID || this.createdBatchID

          this.$emit('samplesBatched')

          if (targetBatchID) {
            this.$router.replace(`/print-tag/batches/${targetBatchID}`)
          }
          break
        }
        case 'PARTIAL':
          this.createdBatchID = summaryTargetBatchID || this.createdBatchID
          this.$emit('samplesBatched')
          this.showResultsView = true
          this.showOverloadView = false
          break
        case 'CONFLICT': {
          const conflictMessage =
            translateBatchErrorFromResponse(this.moveLastError) ||
            'Uma ou mais amostras já estão em outro lote. Atualize a listagem e tente novamente.'
          notify({
            title: resolveConflictToastTitle(this.moveLastError),
            message: conflictMessage,
          })
          this.$emit('samplesBatched')
          break
        }
        case 'OVERLOAD':
          this.showOverloadView = true
          break
        case 'ERROR':
        default: {
          const errorResponse = this.moveLastError
          const translated = translateBatchErrorFromResponse(errorResponse)
          if (translated) {
            notify({ title: 'Erro:', message: translated })
          } else if (errorResponse && errorResponse.status === 422) {
            notify({
              title: 'Erro:',
              message: this.translateBatchSampleValdation(errorResponse),
            })
          } else {
            notify({
              title: 'Erro:',
              message: 'Erro ao tentar criar novo lote.',
            })
          }
          break
        }
      }
    },
    async handleRetryFailed() {
      this.isMoving = true

      const outcome = await this.retryFailed()

      this.isMoving = false

      this.applyOutcome(outcome)
    },
    handleResultsClose() {
      this.showResultsView = false

      if (this.createdBatchID) {
        this.$router.replace(`/print-tag/batches/${this.createdBatchID}`)
      }
    },
    handleOpenBatch(batchIdentifier) {
      const targetBatchID = batchIdentifier || this.createdBatchID

      if (!targetBatchID) return

      this.$router.replace(`/print-tag/batches/${targetBatchID}`)
    },
    handleOverloadClose() {
      this.showOverloadView = false
    },
  }
}
</script>

<style lang="scss" scoped>
.batch-samples {
  font-family: 'Exo 2', sans-serif;
}

.batches-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &-item {
    cursor: pointer;
  }

  &-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;

    &-icon {
      width: 95px;
      height: 87px;
    }

    &-text {
      font-family: Roboto;
      font-weight: 400;
      font-size: 1rem;
      text-align: center;
      color: #666666;
      max-width: 294px;
    }
  }
}

.batches-modal {
  max-height: 400px;
}
</style>
